import { useEffect, useState } from 'react';

import { fetchCourseExercises } from '../courses/courseApi';
import { pickNextExercise } from './pickNextExercise';
import type { CourseExercise } from '../courses/courseApi';

interface NextExerciseState {
  /** The next exercise in the course that the student hasn't completed yet, if any. */
  nextExercise: CourseExercise | null;
  /** True once the sibling exercises have been checked and none remain uncompleted. */
  isCourseComplete: boolean;
  isLoading: boolean;
}

/**
 * Looks up the next not-yet-completed exercise in the same course once an exercise has been
 * passed, so the student can jump straight into it instead of landing on a dead end (T147).
 */
export function useNextExercise(courseId: string | undefined, currentExerciseId: string | undefined, enabled: boolean): NextExerciseState {
  const [nextExercise, setNextExercise] = useState<CourseExercise | null>(null);
  const [isCourseComplete, setIsCourseComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !courseId) {
      return undefined;
    }

    let ignore = false;

    async function loadNextExercise() {
      try {
        setIsLoading(true);
        const course = await fetchCourseExercises(courseId as string);
        if (ignore) {
          return;
        }
        const upcoming = pickNextExercise(course.exercises, currentExerciseId, (candidate) =>
          Boolean(candidate.completed),
        );
        setNextExercise(upcoming ?? null);
        setIsCourseComplete(!upcoming);
      } catch {
        if (!ignore) {
          setNextExercise(null);
          setIsCourseComplete(false);
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    }

    void loadNextExercise();
    return () => {
      ignore = true;
    };
  }, [courseId, currentExerciseId, enabled]);

  return { nextExercise, isCourseComplete, isLoading };
}
